import PropTypes from 'prop-types';
// Date formatting is now handled by backend
import avatarDefault from '../../../../assets/images/avatar-default.svg';
import Icon from '../../../../components/Icon/Icon';

const WhatsAppMessageItem = ({
  contact,
  lastMessage,
  timestamp,
  isSelected = false,
  isUnread = false,
  unreadCount = 0,
  messageType = 'incoming',
  onClick
}) => {
  const getMessageIcon = () => {
    if (messageType === 'outgoing') {
      return (
        <div style={{
          width: '14px',
          height: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon name="message_outgoing" color="#0BA9E0" width={14} height={14} />
        </div>
      );
    } else {
      // Incoming message icon
      const iconColor = isUnread ? '#F1C40F' : '#FF9800';
      return (
        <div style={{
          width: '14px',
          height: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Icon name="message_incoming" color={iconColor} width={14} height={14} />
        </div>
      );
    }
  };

  const getAvatarContent = () => {
    const avatarSrc = contact.avatar || avatarDefault;

    return (
      <img
        src={avatarSrc}
        alt={contact.name}
        style={{
          width: '40px',
          height: '40px',
          objectFit: 'cover'
        }}
        onError={(e) => {
          // Fallback to default avatar if image fails to load
          if (e.target.src !== avatarDefault) {
            e.target.src = avatarDefault;
          }
        }}
      />
    );
  };

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '20px',
    width: '100%',
    backgroundColor: isSelected ? '#EDFDFF' : '#FFFFFF',
    borderBottom: '1px solid #CFD8DC',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  };

  const avatarContainerStyle = {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0
  };

  const contentStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    overflow: 'hidden'
  };

  const headerRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '4px'
  };

  const nameStyle = {
    fontFamily: 'Poppins',
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '1.5em',
    letterSpacing: '0.25%',
    color: isSelected ? '#0BA9E0' : '#344054',
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  };

  const timestampStyle = {
    fontFamily: 'Poppins',
    fontWeight: '500',
    fontSize: '13px',
    lineHeight: '1.5em',
    color: '#737C8A',
    textAlign: 'right',
    flexShrink: 0
  };

  const messageRowStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    width: '100%',
    minWidth: 0
  };

  const messageTextStyle = {
    fontFamily: 'Poppins',
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '1.5em',
    color: '#737C8A',
    margin: 0,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
    maxWidth: '100%'
  };

  const unreadBadgeStyle = {
    backgroundColor: '#38C669',
    borderRadius: '3px',
    padding: '0px 4px',
    minWidth: '16px',
    height: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };

  const unreadCountStyle = {
    fontFamily: 'Poppins',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '1.5em',
    color: '#FFFFFF',
    textAlign: 'center'
  };

  return (
    <div style={containerStyle} onClick={onClick}>
      {/* Avatar */}
      <div style={avatarContainerStyle}>
        {getAvatarContent()}
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Header Row - Name and Timestamp */}
        <div style={headerRowStyle}>
          <h3 style={nameStyle}>{contact.name}</h3>
          <span style={timestampStyle}>{timestamp}</span>
        </div>

        {/* Message Row - Icon, Text, and Unread Badge */}
        <div style={messageRowStyle}>
          {getMessageIcon()}
          <p style={messageTextStyle}>
            {lastMessage && lastMessage.trim() ? lastMessage : 'New message'}
          </p>
          {isUnread && unreadCount > 0 && (
            <div style={unreadBadgeStyle}>
              <span style={unreadCountStyle}>{unreadCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

WhatsAppMessageItem.propTypes = {
  contact: PropTypes.shape({
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string
  }).isRequired,
  lastMessage: PropTypes.string,
  timestamp: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  isUnread: PropTypes.bool,
  unreadCount: PropTypes.number,
  messageType: PropTypes.oneOf(['incoming', 'outgoing']),
  onClick: PropTypes.func
};

export default WhatsAppMessageItem;
